/**
 * Utility functions for working with Google Maps
 */

/**
 * Creates a URL for Google Maps that works across all devices (mobile and desktop)
 * Simplificado para usar apenas URLs web padrão
 * 
 * @param item Object containing Google Maps data (placeId, coordinates, etc.)
 * @returns A string URL or null if no valid location data is found
 */
export function createGoogleMapsUrl(item: any): string | null {
  if (!item) return null;

  // Extract location data from the item
  const placeId = item.placeId || 
                 ('departurePlaceId' in item ? item.departurePlaceId : null) || 
                 ('arrivalPlaceId' in item ? item.arrivalPlaceId : null);
  
  let coordinates = null;
  if (item.coordinates?.lat && item.coordinates?.lng) {
    coordinates = { lat: item.coordinates.lat, lng: item.coordinates.lng };
  } else if ('departureCoordinates' in item && item.departureCoordinates?.lat && item.departureCoordinates?.lng) {
    coordinates = { lat: item.departureCoordinates.lat, lng: item.departureCoordinates.lng };
  } else if ('arrivalCoordinates' in item && item.arrivalCoordinates?.lat && item.arrivalCoordinates?.lng) {
    coordinates = { lat: item.arrivalCoordinates.lat, lng: item.arrivalCoordinates.lng };
  }

  // Find a suitable query name
  const queryName = item.name || 
                   item.title || 
                   item.description?.slice(0, 30) || 
                   (item.departureName || item.arrivalName) || 
                   'Local';
  
  // Ensure the query is properly encoded
  const encodedQuery = encodeURIComponent(queryName);

  // If a pre-defined URL is provided, check and fix it if needed
  let predefinedUrl = item.googleMapsUrl || 
                     ('departureGoogleMapsUrl' in item ? item.departureGoogleMapsUrl : null) || 
                     ('arrivalGoogleMapsUrl' in item ? item.arrivalGoogleMapsUrl : null);
  
  // Verificar e corrigir URLs no formato antigo
  if (predefinedUrl) {
    const fixedUrl = fixGoogleMapsUrl(predefinedUrl, queryName);
    if (fixedUrl) return fixedUrl;
  }
  
  // Standard Google Maps URLs
  if (placeId) {
    // URL padrão com place_id - funciona em todos os dispositivos
    return `https://www.google.com/maps/search/?api=1&query=${encodedQuery}&query_place_id=${placeId}`;
  } else if (coordinates) {
    // URL padrão com coordenadas - funciona em todos os dispositivos
    return `https://www.google.com/maps/search/?api=1&query=${coordinates.lat},${coordinates.lng}`;
  }
  
  return null; // No valid location data found
}

/**
 * Opens Google Maps for a location with support for all devices
 * @param item Object containing Google Maps data (placeId, coordinates, etc.)
 * @returns true if navigation was attempted, false if no valid location data found
 */
export function openInGoogleMaps(item: any): boolean {
  const url = createGoogleMapsUrl(item);
  
  if (!url) return false;
  
  // Abre diretamente no navegador, que redirecionará para o app se estiver instalado
  window.location.href = url;
  
  return true;
}

/**
 * Creates only a web URL for Google Maps that works on all platforms
 * (Mantido por compatibilidade, mas na prática idêntico ao createGoogleMapsUrl agora)
 */
export function createGoogleMapsWebUrl(item: any): string | null {
  return createGoogleMapsUrl(item);
}

/**
 * Verifica e corrige um URL do Google Maps no formato antigo
 * para o formato correto usando query_place_id
 */
export function fixGoogleMapsUrl(url: string | undefined | null, itemName?: string): string | null {
  if (!url) return null;
  
  // Verificar se é uma URL no formato antigo (com place_id: dentro do parâmetro de consulta)
  if (url.includes('place_id:')) {
    // Extrair o place_id
    const placeIdMatch = url.match(/place_id:([^&\s]+)/);
    if (placeIdMatch && placeIdMatch[1]) {
      const placeId = placeIdMatch[1];
      const query = itemName || 'Local';
      
      // Construir uma nova URL no formato correto
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}&query_place_id=${placeId}`;
    }
  }
  
  // Se não for uma URL no formato antigo ou não conseguirmos extrair o place_id, retornamos a URL original
  return url;
} 